import { api, ApiError } from "../api.js";
import { esc, siteLocationLabel, toast } from "../utils.js";
import { navigate } from "../app.js";

/** The Subsurface dive log's <divesites> element, cut out of the file as
 * text, or null when there isn't one.
 *
 * Only this part is uploaded. The rest of a log — every dive, who it was
 * with, the depth profiles — stays on the diver's computer, and a log with
 * years of profiles is far larger than the server accepts anyway. Plain
 * string search rather than parsing: the server parses (and checks) what
 * arrives, and this only has to find where the element starts and ends. */
export function divesitesSection(text) {
  const start = text.search(/<divesites[\s>/]/);
  if (start < 0) return null;
  const close = "</divesites>";
  const end = text.lastIndexOf(close);
  if (end < start) {
    // <divesites/> (none), or a file cut short: send from the start and
    // let the server say which.
    return text.slice(start);
  }
  return text.slice(start, end + close.length);
}

function formatMetres(m) {
  return m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1)} km`;
}

function siteLink(match) {
  return `<a href="#/dive-site/${match.id}">${esc(match.name)}</a>`;
}

const GROUPS = [
  { status: "new", title: "New", hint: "Not on DiveCohorts yet." },
  {
    status: "nearby",
    title: "Near a site already here",
    hint: "Possibly the same site under another name. Tick any that are different sites.",
  },
  { status: "exists", title: "Already on DiveCohorts", hint: "These won't be added again." },
  { status: "invalid", title: "Can't be imported", hint: null },
];

function itemHtml(item) {
  const site = item.site;
  const choosable = item.status === "new" || item.status === "nearby";
  const location = siteLocationLabel(site);
  let detail = "";
  if (item.status === "nearby") {
    detail = `<div class="small text-slate">Near ${item.matches
      .map((m) => `${siteLink(m)} (${formatMetres(m.distance_m)})`)
      .join(", ")}</div>`;
  } else if (item.status === "exists") {
    detail = `<div class="small text-slate">As ${siteLink(item.matches[0])}</div>`;
  } else if (item.status === "invalid") {
    detail = `<div class="small text-danger">${esc(item.reason)}</div>`;
  }
  const name = esc(site.name || "(no name)");
  const label = `
      <div class="fw-semibold">${name}</div>
      ${location ? `<div class="small text-slate"><i class="bi bi-geo-alt me-1"></i>${esc(location)}</div>` : ""}
      ${detail}`;
  if (!choosable) {
    return `<li class="list-group-item" data-index="${item.index}">${label}</li>`;
  }
  const id = `import-site-${item.index}`;
  return `
    <li class="list-group-item" data-index="${item.index}">
      <div class="form-check">
        <input class="form-check-input" type="checkbox" id="${id}" data-index="${item.index}"
          ${item.status === "new" ? "checked" : ""} />
        <label class="form-check-label" for="${id}">${label}</label>
      </div>
    </li>`;
}

function groupsHtml(items) {
  return GROUPS.map(({ status, title, hint }) => {
    const group = items.filter((i) => i.status === status);
    if (!group.length) return "";
    return `
      <section class="mb-4" data-group="${status}">
        <h2 class="h6 fw-semibold mb-1">${title} (${group.length})</h2>
        ${hint ? `<div class="small text-slate mb-2">${hint}</div>` : ""}
        <ul class="list-group">${group.map(itemHtml).join("")}</ul>
      </section>`;
  }).join("");
}

function resultHtml(result) {
  const created = result.created.length;
  const skipped = result.skipped.length;
  return `
    <div class="alert alert-success" id="import-done">
      Added ${created} dive site${created === 1 ? "" : "s"}.
      ${skipped ? `${skipped} ${skipped === 1 ? "was" : "were"} already on DiveCohorts.` : ""}
    </div>
    ${created ? `<ul class="list-group mb-3">${result.created
      .map((s) => `<li class="list-group-item"><a href="#/dive-site/${s.id}">${esc(s.name)}</a></li>`)
      .join("")}</ul>` : ""}
    <div class="d-flex gap-2">
      <button class="btn btn-primary" id="import-finished">Back to dive sites</button>
      <button class="btn btn-outline-secondary" id="import-another">Import another file</button>
    </div>`;
}

export function render(container) {
  container.innerHTML = `
    <button class="btn btn-link ps-0 mb-2" id="back-btn"><i class="bi bi-arrow-left"></i> Back</button>
    <h1 class="display-heading h3 mb-2">Import from Subsurface</h1>
    <p class="text-slate mb-3">
      Choose your Subsurface dive log (the <code>.ssrf</code> file Subsurface saves, or an XML export).
      Only its dive sites are sent; your dives stay on this computer. You'll see what will be added before anything is.
    </p>
    <div class="mb-3">
      <label class="form-label" for="import-file">Dive log</label>
      <input type="file" class="form-control" id="import-file" accept=".ssrf,.xml,application/xml,text/xml" />
    </div>
    <div id="import-error" class="text-danger small mb-3 d-none"></div>
    <div id="import-status" class="text-slate small mb-3"></div>
    <div id="import-preview"></div>`;

  const fileInput = container.querySelector("#import-file");
  const errorEl = container.querySelector("#import-error");
  const statusEl = container.querySelector("#import-status");
  const previewEl = container.querySelector("#import-preview");
  let items = [];

  function showError(message) {
    errorEl.textContent = message;
    errorEl.classList.toggle("d-none", !message);
  }

  function chosenSites() {
    return [...previewEl.querySelectorAll("input[type=checkbox][data-index]:checked")].map(
      (box) => items.find((i) => String(i.index) === box.dataset.index).site
    );
  }

  function updateImportButton() {
    const button = previewEl.querySelector("#import-submit");
    if (!button) return;
    const count = chosenSites().length;
    button.disabled = count === 0;
    button.textContent = `Import ${count} dive site${count === 1 ? "" : "s"}`;
  }

  function reset() {
    items = [];
    fileInput.value = "";
    fileInput.disabled = false;
    previewEl.innerHTML = "";
    statusEl.textContent = "";
    showError("");
  }

  function showPreview() {
    const count = (status) => items.filter((i) => i.status === status).length;
    const choosable = count("new") + count("nearby");
    statusEl.textContent = `Found ${items.length} dive site${items.length === 1 ? "" : "s"} in the file.`;
    previewEl.innerHTML = `
      ${groupsHtml(items)}
      <div class="d-flex gap-2 mb-4">
        ${choosable ? `<button class="btn btn-primary" id="import-submit"></button>` : ""}
        <button class="btn btn-outline-secondary" id="import-cancel">${choosable ? "Cancel" : "Choose another file"}</button>
      </div>`;
    previewEl.querySelectorAll("input[type=checkbox]").forEach((box) =>
      box.addEventListener("change", updateImportButton)
    );
    previewEl.querySelector("#import-cancel").addEventListener("click", reset);
    previewEl.querySelector("#import-submit")?.addEventListener("click", submit);
    updateImportButton();
  }

  async function submit() {
    const button = previewEl.querySelector("#import-submit");
    button.disabled = true;
    showError("");
    try {
      const result = await api.post("/dive-sites/import", { sites: chosenSites() });
      fileInput.disabled = true;
      statusEl.textContent = "";
      previewEl.innerHTML = resultHtml(result);
      previewEl.querySelector("#import-finished").addEventListener("click", () => navigate("/dive-sites"));
      previewEl.querySelector("#import-another").addEventListener("click", reset);
      toast(`Added ${result.created.length} dive site${result.created.length === 1 ? "" : "s"}.`, "success");
    } catch (err) {
      showError(err instanceof ApiError ? err.message : "Something went wrong.");
      updateImportButton();
    }
  }

  fileInput.addEventListener("change", async () => {
    const file = fileInput.files?.[0];
    previewEl.innerHTML = "";
    showError("");
    if (!file) return;

    let section;
    try {
      section = divesitesSection(await file.text());
    } catch {
      showError("That file couldn't be read.");
      return;
    }
    if (section === null) {
      showError("That file has no dive sites in it.");
      return;
    }

    statusEl.textContent = "Reading the dive sites…";
    fileInput.disabled = true;
    try {
      const body = await api.post("/dive-sites/import/preview", { xml: section });
      items = body.sites;
      showPreview();
    } catch (err) {
      statusEl.textContent = "";
      showError(err instanceof ApiError ? err.message : "Something went wrong.");
    } finally {
      fileInput.disabled = false;
    }
  });

  container.querySelector("#back-btn").addEventListener("click", () => history.back());
}
